import React, { useMemo, useState } from "react";
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DialogContentModal from "../../../../../../../templates/ApplicationDetails/components/DialogContent";
import ApplicationTable from "./ApplicationTable";

dayjs.extend(customParseFormat);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1f4a7c",
    color: theme.palette.common.white,
    fontSize: 15,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#1f4a7c1c",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

/**
 * InProgressWithOfficerModal
 *
 * Renders a two-column summary table: Status | Files Count
 * Clicking the Files Count opens a nested modal with the full ApplicationTable
 * filtered to that specific status.
 *
 * @param {Array}    dashboardData       - full raw data from _bidashboard API
 * @param {Function} onApplicationSelect - passed down to ApplicationTable for row-click navigation
 */
const InProgressWithOfficerModal = ({ dashboardData = [], onApplicationSelect }) => {
  const [selectedStatus, setSelectedStatus] = useState(null); // status whose count was clicked

  // Filter records that are "In Progress With Officers"
  const inProgressRecords = useMemo(
    () => dashboardData.filter((item) => item.mainDashBoardStatus?.toLowerCase()?.includes("in progress with officer")),
    [dashboardData]
  );

  const getPendingDays = (date) => {
    if (!date) return -1;

    const parsedDate = dayjs(date, "DD-MM-YYYY", true);
    const validDate = parsedDate.isValid() ? parsedDate : dayjs(date);

    if (!validDate.isValid()) return -1;
    return dayjs().diff(validDate.startOf("day"), "day");
  };

  // Group by `status` field and count, including pending > 15 days
  const statusSummary = useMemo(() => {
    const map = {};

    inProgressRecords.forEach((item) => {
      const statusKey = item.status || "Unknown";
      if (!map[statusKey]) {
        map[statusKey] = { count: 0, pending15Count: 0 };
      }

      map[statusKey].count += 1;
      if (getPendingDays(item?.lastCreatedTime) >= 15) {
        map[statusKey].pending15Count += 1;
      }
    });

    return Object.entries(map)
      .map(([status, { count, pending15Count }]) => ({ status, count, pending15Count }))
      .sort((a, b) => b.count - a.count);
  }, [inProgressRecords]);

  const totalFiles = inProgressRecords.length;

  // console.log("InProgressWithOfficerModal", { totalFiles, statusSummary, inProgressRecords });

  // Records for the nested detail modal (filtered to the clicked status)
  const detailRecords = useMemo(() => {
    if (!selectedStatus) return [];
    return inProgressRecords.filter((item) => (item.status || "Unknown") === selectedStatus);
  }, [selectedStatus, inProgressRecords]);

  console.log("Detail Records", detailRecords);

  // Stub state handlers for ApplicationTable (search handled inside that component)
  const [fastPasIdSearch, setFastPasIdSearch] = useState("");

  return (
    <Box sx={{ p: 2, minWidth: 400 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, color: "#444", fontWeight: 600 }}>
        Total In Progress Files: <span style={{ color: "#1976d2", fontWeight: 700 }}>{totalFiles}</span>
      </Typography>

      {statusSummary.length === 0 ? (
        <Typography sx={{ textAlign: "center", color: "#999", py: 4 }}>No records found</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: "8px", overflow: "hidden" }}>
          <Table aria-label="in-progress-with-officer-table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell align="center">Files Count</StyledTableCell>
                <StyledTableCell align="center">No of files Pending &gt; 15 Days</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statusSummary.map((row, index) => (
                <StyledTableRow key={row.status}>
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell>{row.status}</StyledTableCell>
                  <StyledTableCell align="center">
                    {/* Clicking the count opens the nested full-table modal */}
                    <Box
                      onClick={() => setSelectedStatus(row.status)}
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#1976d220",
                        color: "#1976d2",
                        fontWeight: 700,
                        borderRadius: "12px",
                        px: 1.5,
                        py: 0.25,
                        minWidth: 36,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "background 0.2s",
                        "&:hover": {
                          backgroundColor: "#1976d240",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {row.count}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#FFF4CC",
                        color: "#9A6700",
                        fontWeight: 700,
                        borderRadius: "12px",
                        px: 1.5,
                        py: 0.25,
                        minWidth: 36,
                        textAlign: "center",
                      }}
                    >
                      {row.pending15Count}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Nested modal: full ApplicationTable for the clicked status */}
      {selectedStatus && (
        <DialogContentModal
          dataOpen={!!selectedStatus}
          onClose={() => setSelectedStatus(null)}
          title={`In Progress — ${selectedStatus} (${detailRecords.length} records)`}
        >
          <ApplicationTable
            dashboardData={detailRecords}
            filters={{}}
            showMs={false}
            setshowMs={() => {}}
            fastPasIdSearch={fastPasIdSearch}
            setFastPasIdSearch={setFastPasIdSearch}
            loading={false}
            onApplicationSelect={onApplicationSelect}
            isFromProgress={true}
          />
        </DialogContentModal>
      )}
    </Box>
  );
};

export default InProgressWithOfficerModal;
